// Admin panel API client (P0: overview + users).
//
// Platform-scoped queries: no workspaceId in keys or paths — the boundary is
// the platform, and hooks gate on `isPlatformAdmin` instead of `scope !== null`
// (plan §2.2). The api-client still sends X-Workspace-Id; admin routes ignore it.
import { apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';

export type PlatformRole = 'user' | 'admin';

export interface AdminUserListItem {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  isBlocked: boolean;
  platformRole: PlatformRole;
  createdAt: string;
  activeWorkspaces: number;
  lastActivityAt: string | null;
}

export interface AdminUserMembership {
  id: string;
  role: string;
  status: string;
  joinedAt: string;
  workspace: { id: string; name: string; slug: string };
}

export interface AdminUserSession {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    name: string;
    emailVerified: boolean;
    isBlocked: boolean;
    platformRole: PlatformRole;
    createdAt: string;
  };
  memberships: AdminUserMembership[];
  sessions: AdminUserSession[];
}

/**
 * Display view of a backend worker health snapshot. Workers ship slightly
 * different shapes (tick counters, telegram extras, outbox `configured`);
 * the shared fields drive the WorkerHealthCard, extras are optional.
 */
export interface AdminWorkerHealth {
  status: string;
  running: boolean;
  tickInProgress?: boolean;
  startedAt?: string | null;
  lastTickStartedAt?: string | null;
  lastTickFinishedAt?: string | null;
  ticksStarted?: number;
  ticksCompleted?: number;
  tickErrors?: number;
  consecutiveErrors?: number;
  lastSuccessAt?: string | null;
  lastFailureAt?: string | null;
}

export interface AdminOverview {
  counters: {
    users: number;
    users24h: number;
    workspaces: number;
    deals: number;
  };
  messages24h: {
    count: number;
    tokens: number;
  };
  llmSpend: {
    periodDays: number;
    messages: number;
    tokens: number;
    /** Decimal serialized as string by the API (precision-safe). */
    costRub: string;
  };
  outbox: Array<{ status: string; count: number }>;
  workers: {
    scenarioRunner: AdminWorkerHealth;
    telegramInbound: AdminWorkerHealth;
    telegramDelivery: AdminWorkerHealth;
    mailOutbox: AdminWorkerHealth & { configured?: boolean };
    externalCalendarSync: AdminWorkerHealth;
  };
}

export interface AdminUsersFilters {
  search?: string;
  page: number;
  pageSize: number;
}

export interface AdminUsersPage {
  users: AdminUserListItem[];
  total: number;
}

// ─── Query keys ───────────────────────────────────────────────────────────────

/** Root admin key: invalidation of any admin data goes through this prefix. */
export const ADMIN_QUERY_ROOT = 'admin' as const;

export function getAdminOverviewQueryKey(periodDays: number): readonly (string | number)[] {
  return [ADMIN_QUERY_ROOT, 'overview', periodDays] as const;
}

export function getAdminUsersQueryKey(filters: AdminUsersFilters): readonly (string | number)[] {
  return [ADMIN_QUERY_ROOT, 'users', filters.search ?? '', filters.page, filters.pageSize] as const;
}

export function getAdminUserDetailQueryKey(userId: string): readonly string[] {
  return [ADMIN_QUERY_ROOT, 'users', 'detail', userId] as const;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function getAdminOverview(periodDays = 7): Promise<AdminOverview> {
  return apiGet<AdminOverview>(`/admin/overview?periodDays=${periodDays}`);
}

export async function listAdminUsers(filters: AdminUsersFilters): Promise<AdminUsersPage> {
  const params = new URLSearchParams({
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });
  const search = filters.search?.trim();
  if (search) params.set('search', search);
  return apiGet<AdminUsersPage>(`/admin/users?${params.toString()}`);
}

export async function getAdminUserById(userId: string): Promise<AdminUserDetail> {
  return apiGet<AdminUserDetail>(`/admin/users/${userId}`);
}

export async function verifyAdminUserEmail(userId: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, Record<string, never>>(`/admin/users/${userId}/verify`, {});
}

export async function blockAdminUser(userId: string, reason?: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, { reason?: string }>(`/admin/users/${userId}/block`, {
    ...(reason === undefined ? {} : { reason }),
  });
}

export async function unblockAdminUser(userId: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, Record<string, never>>(`/admin/users/${userId}/unblock`, {});
}

export async function sendAdminUserPasswordReset(userId: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, Record<string, never>>(`/admin/users/${userId}/send-reset`, {});
}

export async function setAdminUserPlatformRole(
  userId: string,
  role: PlatformRole,
): Promise<{ ok: true }> {
  return apiPatch<{ ok: true }, { role: PlatformRole }>(`/admin/users/${userId}/platform-role`, {
    role,
  });
}

// ─── Workspaces (P1) ──────────────────────────────────────────────────────────

export interface AdminWorkspaceOwner {
  id: string;
  email: string;
  name: string;
}

export interface AdminWorkspaceListItem {
  id: string;
  slug: string;
  name: string;
  kind: string;
  plan: string;
  isBlocked: boolean;
  createdAt: string;
  owner: AdminWorkspaceOwner | null;
  activeMembers: number;
  deals: number;
  /** Decimal serialized as string by the API (precision-safe). */
  spendRub: string;
}

export interface AdminWorkspaceMemberRow {
  id: string;
  role: string;
  status: string;
  joinedAt: string;
  user: { id: string; email: string; name: string };
}

export interface AdminWorkspaceDetail {
  workspace: {
    id: string;
    slug: string;
    name: string;
    kind: string;
    plan: string;
    isBlocked: boolean;
    timezone: string;
    createdAt: string;
    owner: AdminWorkspaceOwner | null;
  };
  members: AdminWorkspaceMemberRow[];
  counters: {
    activeMembers: number;
    deals: number;
    contacts: number;
    tasks: number;
    messages: number;
  };
  spend: {
    messages: number;
    tokens: number;
    /** Decimal serialized as string by the API (precision-safe). */
    costRub: string;
  };
}

export interface AdminWorkspacesFilters {
  search?: string;
  page: number;
  pageSize: number;
}

export interface AdminWorkspacesPage {
  workspaces: AdminWorkspaceListItem[];
  total: number;
}

export function getAdminWorkspacesQueryKey(
  filters: AdminWorkspacesFilters,
): readonly (string | number)[] {
  return [
    ADMIN_QUERY_ROOT,
    'workspaces',
    filters.search ?? '',
    filters.page,
    filters.pageSize,
  ] as const;
}

export function getAdminWorkspaceDetailQueryKey(workspaceId: string): readonly string[] {
  return [ADMIN_QUERY_ROOT, 'workspaces', 'detail', workspaceId] as const;
}

export async function listAdminWorkspaces(
  filters: AdminWorkspacesFilters,
): Promise<AdminWorkspacesPage> {
  const params = new URLSearchParams({
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });
  const search = filters.search?.trim();
  if (search) params.set('search', search);
  return apiGet<AdminWorkspacesPage>(`/admin/workspaces?${params.toString()}`);
}

export async function getAdminWorkspaceById(workspaceId: string): Promise<AdminWorkspaceDetail> {
  return apiGet<AdminWorkspaceDetail>(`/admin/workspaces/${workspaceId}`);
}

export async function blockAdminWorkspace(
  workspaceId: string,
  reason?: string,
): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, { reason?: string }>(`/admin/workspaces/${workspaceId}/block`, {
    ...(reason === undefined ? {} : { reason }),
  });
}

export async function unblockAdminWorkspace(workspaceId: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, Record<string, never>>(
    `/admin/workspaces/${workspaceId}/unblock`,
    {},
  );
}

// ─── Plan configs (P0-2: admin-managed plans) ─────────────────────────────────

export interface AdminPlanConfig {
  id: string;
  code: string;
  displayName: string;
  priceCents: number;
  costCents: number;
  /** BigInt serialized as string by the API (precision-safe). */
  tokenLimitMonthly: string;
  /** BigInt serialized as string by the API (precision-safe). */
  storageLimitBytes: string;
  billingPeriod: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdminPlanInput {
  code: string;
  displayName: string;
  priceCents: number;
  costCents: number;
  tokenLimitMonthly: number;
  storageLimitBytes: number;
  billingPeriod: string;
  sortOrder: number;
}

export interface UpdateAdminPlanInput {
  displayName?: string | undefined;
  priceCents?: number | undefined;
  costCents?: number | undefined;
  tokenLimitMonthly?: number | undefined;
  storageLimitBytes?: number | undefined;
  billingPeriod?: string | undefined;
  isActive?: boolean | undefined;
  sortOrder?: number | undefined;
}

export function getAdminPlansQueryKey(): readonly string[] {
  return [ADMIN_QUERY_ROOT, 'plans'] as const;
}

export async function listAdminPlans(): Promise<AdminPlanConfig[]> {
  const page = await apiGet<{ plans: AdminPlanConfig[] }>('/admin/plans');
  return page.plans;
}

export async function createAdminPlan(input: CreateAdminPlanInput): Promise<AdminPlanConfig> {
  return apiPost<AdminPlanConfig, CreateAdminPlanInput>('/admin/plans', input);
}

export async function updateAdminPlan(
  planId: string,
  input: UpdateAdminPlanInput,
): Promise<AdminPlanConfig> {
  return apiPatch<AdminPlanConfig, UpdateAdminPlanInput>(`/admin/plans/${planId}`, input);
}

export async function deactivateAdminPlan(planId: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, Record<string, never>>(`/admin/plans/${planId}/deactivate`, {});
}

// ─── LLM monitoring (P1) ───────────────────────────────────────────────────────

/** Period window for LLM aggregations; ISO strings, bounded to 90 days. */
export interface LlmPeriod {
  from: string;
  to: string;
}

export interface LlmSummary {
  from: string;
  to: string;
  messages: number;
  tokens: number;
  /** Decimal serialized as string by the API (precision-safe). */
  costRub: string;
}

export interface LlmByWorkspaceRow {
  /** null = messages written before workspace scoping existed. */
  workspaceId: string | null;
  workspaceName: string | null;
  workspaceSlug: string | null;
  messages: number;
  tokens: number;
  costRub: string;
}

export interface LlmByProfileRow {
  profile: string;
  conversations: number;
  messages: number;
  tokens: number;
  costRub: string;
}

export interface LlmTopConversationRow {
  conversationId: string;
  title: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  userEmail: string | null;
  messages: number;
  tokens: number;
  costRub: string;
  lastMessageAt: string | null;
}

export type LlmView = 'summary' | 'by-workspace' | 'by-profile' | 'top-conversations';

export function getAdminLlmQueryKey(view: LlmView, period: LlmPeriod): readonly string[] {
  return [ADMIN_QUERY_ROOT, 'llm', view, period.from, period.to] as const;
}

function llmPeriodParams(period: LlmPeriod): string {
  const params = new URLSearchParams({ from: period.from, to: period.to });
  return params.toString();
}

export async function getAdminLlmSummary(period: LlmPeriod): Promise<LlmSummary> {
  return apiGet<LlmSummary>(`/admin/llm/summary?${llmPeriodParams(period)}`);
}

export async function getAdminLlmByWorkspace(period: LlmPeriod): Promise<LlmByWorkspaceRow[]> {
  return apiGet<LlmByWorkspaceRow[]>(`/admin/llm/by-workspace?${llmPeriodParams(period)}`);
}

export async function getAdminLlmByProfile(period: LlmPeriod): Promise<LlmByProfileRow[]> {
  return apiGet<LlmByProfileRow[]>(`/admin/llm/by-profile?${llmPeriodParams(period)}`);
}

export async function getAdminLlmTopConversations(
  period: LlmPeriod,
): Promise<LlmTopConversationRow[]> {
  return apiGet<LlmTopConversationRow[]>(
    `/admin/llm/top-conversations?${llmPeriodParams(period)}&limit=20`,
  );
}

// ─── Mail outbox (P2) ──────────────────────────────────────────────────────────

export type OutboxStatus =
  | 'pending'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'bounced'
  | 'spam_complaint'
  | 'failed';

export type OutboxKind =
  | 'meeting_invited'
  | 'meeting_updated'
  | 'meeting_cancelled'
  | 'workspace_invited'
  | 'password_reset'
  | 'email_verification'
  | 'agent_email';

export interface AdminOutboxWorkspace {
  id: string;
  name: string;
  slug: string;
}

export interface AdminOutboxListItem {
  id: string;
  kind: OutboxKind;
  status: OutboxStatus;
  toEmail: string;
  toName: string | null;
  subject: string;
  attempts: number;
  availableAt: string;
  lastError: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  workspace: AdminOutboxWorkspace;
}

/**
 * Letter detail. Body text is NOT part of the P2 API surface — minimal PII
 * (plan §7.3); the list and the detail projections both stay body-free.
 */
export interface AdminOutboxDetail extends AdminOutboxListItem {
  updatedAt: string;
  dedupeKey: string;
  providerMessageId: string | null;
  calendarEventId: string | null;
}

export interface AdminOutboxStatusCounts {
  /** All seven statuses, zero-filled when the backend has none of them. */
  statuses: Array<{ status: OutboxStatus; count: number }>;
}

export interface AdminOutboxFilters {
  status?: OutboxStatus;
  kind?: OutboxKind;
  search?: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

export interface AdminOutboxPage {
  messages: AdminOutboxListItem[];
  total: number;
}

export function getAdminOutboxQueryKey(filters: AdminOutboxFilters): readonly (string | number)[] {
  return [
    ADMIN_QUERY_ROOT,
    'mail',
    'outbox',
    filters.status ?? 'all',
    filters.kind ?? 'all',
    filters.search ?? '',
    filters.from ?? '',
    filters.to ?? '',
    filters.page,
    filters.pageSize,
  ] as const;
}

export function getAdminOutboxStatusCountsQueryKey(): readonly string[] {
  return [ADMIN_QUERY_ROOT, 'mail', 'status-counts'] as const;
}

export function getAdminOutboxDetailQueryKey(messageId: string): readonly string[] {
  return [ADMIN_QUERY_ROOT, 'mail', 'outbox', 'detail', messageId] as const;
}

export async function listAdminOutbox(filters: AdminOutboxFilters): Promise<AdminOutboxPage> {
  const params = new URLSearchParams({
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });
  if (filters.status !== undefined) params.set('status', filters.status);
  if (filters.kind !== undefined) params.set('kind', filters.kind);
  const search = filters.search?.trim();
  if (search) params.set('search', search);
  if (filters.from !== undefined) params.set('from', filters.from);
  if (filters.to !== undefined) params.set('to', filters.to);
  return apiGet<AdminOutboxPage>(`/admin/mail/outbox?${params.toString()}`);
}

export async function getAdminOutboxStatusCounts(): Promise<AdminOutboxStatusCounts> {
  return apiGet<AdminOutboxStatusCounts>('/admin/mail/status-counts');
}

export async function getAdminOutboxById(messageId: string): Promise<AdminOutboxDetail> {
  return apiGet<AdminOutboxDetail>(`/admin/mail/outbox/${messageId}`);
}

export async function retryAdminOutboxMessage(messageId: string): Promise<{ ok: true }> {
  return apiPost<{ ok: true }, Record<string, never>>(`/admin/mail/outbox/${messageId}/retry`, {});
}

// ─── Audit log (P2) ────────────────────────────────────────────────────────────

export interface AdminAuditActor {
  id: string;
  email: string;
  name: string;
}

export interface AdminAuditLogEntry {
  id: string;
  actorUserId: string;
  actor: AdminAuditActor;
  /** Machine-readable action, e.g. `user.block`; unknown ones render raw. */
  action: string;
  targetType: string;
  targetId: string | null;
  /** Raw JSON value; rendered as a tooltip, `null` when the action had none. */
  metadata: unknown;
  createdAt: string;
}

export interface AdminAuditFilters {
  action?: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

export interface AdminAuditPage {
  entries: AdminAuditLogEntry[];
  total: number;
}

export function getAdminAuditQueryKey(filters: AdminAuditFilters): readonly (string | number)[] {
  return [
    ADMIN_QUERY_ROOT,
    'audit',
    filters.action ?? 'all',
    filters.from ?? '',
    filters.to ?? '',
    filters.page,
    filters.pageSize,
  ] as const;
}

export async function listAdminAuditLog(filters: AdminAuditFilters): Promise<AdminAuditPage> {
  const params = new URLSearchParams({
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });
  if (filters.action !== undefined && filters.action.length > 0) {
    params.set('action', filters.action);
  }
  if (filters.from !== undefined) params.set('from', filters.from);
  if (filters.to !== undefined) params.set('to', filters.to);
  return apiGet<AdminAuditPage>(`/admin/audit?${params.toString()}`);
}
